class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.scrollTimeout = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.thumbnails = this.querySelectorAll(".gallery-thumbnail");
    this.mainItems = this.querySelectorAll(".gallery-main-item");
    this.mainContainer = this.querySelector(".gallery-main");

    this.setupThumbnailNavigation();
    this.setupScrollTracking();
    this.setupResizeHandler();
  }

  setupThumbnailNavigation() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", () => {
        this.navigateToImage(index);
        this.setActiveThumbnail(index);
      });
    });
  }

  navigateToImage(index) {
    const targetItem = this.mainItems[index];
    if (!targetItem) return;

    if (window.innerWidth >= 768) {
      // Desktop: Scroll page to target image
      const offsetTop = targetItem.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: offsetTop - 20,
        behavior: "smooth",
      });
    } else {
      // Mobile: Scroll horizontal container to target image
      if (this.mainContainer) {
        const scrollLeft = targetItem.offsetLeft - 20;
        this.mainContainer.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  }

  setActiveThumbnail(activeIndex) {
    this.thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle("active", index === activeIndex);
    });
  }

  trackDesktopScroll() {
    if (window.innerWidth < 768) return;

    const viewportCenter = window.pageYOffset + window.innerHeight / 2;
    let activeItem = null;
    let minDistance = Infinity;

    this.mainItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemTop = rect.top + window.pageYOffset;
      const itemBottom = itemTop + rect.height;
      const itemCenter = itemTop + rect.height / 2;

      if (itemTop <= viewportCenter && itemBottom >= window.pageYOffset) {
        const distance = Math.abs(itemCenter - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          activeItem = index;
        }
      }
    });

    if (activeItem !== null) {
      this.setActiveThumbnail(activeItem);
    }
  }

  trackMobileScroll() {
    if (window.innerWidth >= 768 || !this.mainContainer) return;

    const scrollLeft = this.mainContainer.scrollLeft;
    const containerWidth = this.mainContainer.clientWidth;
    const centerPoint = scrollLeft + containerWidth / 2;

    let activeItem = null;
    let minDistance = Infinity;

    this.mainItems.forEach((item, index) => {
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      const itemCenter = itemLeft + item.offsetWidth / 2;

      if (itemLeft <= centerPoint && itemRight >= scrollLeft) {
        const distance = Math.abs(itemCenter - centerPoint);
        if (distance < minDistance) {
          minDistance = distance;
          activeItem = index;
        }
      }
    });

    if (activeItem !== null) {
      this.setActiveThumbnail(activeItem);
    }
  }

  setupScrollTracking() {
    const handleScroll = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.trackDesktopScroll(), 100);
    };

    const handleMobileScroll = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.trackMobileScroll(), 100);
    };

    window.addEventListener("scroll", handleScroll);
    
    if (this.mainContainer) {
      this.mainContainer.addEventListener("scroll", handleMobileScroll);
    }

    // Store references for cleanup
    this.handleScroll = handleScroll;
    this.handleMobileScroll = handleMobileScroll;
  }

  setupResizeHandler() {
    const handleResize = () => {
      setTimeout(() => {
        if (window.innerWidth >= 768) {
          this.trackDesktopScroll();
        } else {
          this.trackMobileScroll();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    this.handleResize = handleResize;
  }

  cleanup() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
    
    if (this.handleMobileScroll && this.mainContainer) {
      this.mainContainer.removeEventListener("scroll", this.handleMobileScroll);
    }
    
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
  }
}

customElements.define('product-gallery', ProductGallery);
